import { apiClient } from '@/api/client';
import { AdminUserCreateDTORole, User } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { FormErrorMessage } from '@/components/ui/FormErrorMessage';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { queryKeys } from '@/lib/queryKeys';
import { PLATFORM_ROLES } from '@/lib/roles';
import { UserFormData } from '@/pages/users/hooks/useUserMutations';
import { mdiAccount, mdiCheck, mdiClose } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

interface UserFormProps {
  user?: User | null;
  onSubmit: (data: UserFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const getFormValues = (
  user?: User | null,
  authProfile?: { email: string; phone: string } | null,
): UserFormData => ({
  email: authProfile?.email || '',
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  phone: authProfile?.phone || '',
  role:
    (user?.roles?.[0] as AdminUserCreateDTORole) || AdminUserCreateDTORole.user,
  enabled: user?.enabled ?? true,
});

export const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!user;

  const { data: authProfile, isLoading: isAuthProfileLoading } = useQuery({
    queryKey: [queryKeys.ADMIN_USER, user?.id, 'auth-profile'],
    queryFn: async () => {
      const response = await apiClient.GET(
        '/admin/users/{id}/auth-profile' as '/admin/users/{id}',
        {
          params: { path: { id: user!.id! } },
        },
      );
      return response.data as unknown as { email: string; phone: string };
    },
    enabled: isEditing && !!user?.id,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
  } = useForm<UserFormData>({
    defaultValues: getFormValues(user, null),
    mode: 'onChange',
  });

  React.useEffect(() => {
    if (user) {
      reset(getFormValues(user, authProfile));
    }
  }, [user, authProfile, reset]);

  const isEnabled = watch('enabled');

  const handleFormSubmit = async (data: UserFormData) => {
    await onSubmit(data);
  };

  return (
    <div className='p-6'>
      <div className='flex items-center space-x-3 mb-6 pb-4 border-b border-grey-200'>
        <div className='w-12 h-12 bg-grey-100 rounded-full flex items-center justify-center'>
          {isEditing && user?.firstName && user?.lastName ? (
            <span className='text-grey-700 font-semibold text-lg'>
              {user.firstName[0]}
              {user.lastName[0]}
            </span>
          ) : (
            <div className='w-6 h-6 text-grey-600'>
              <svg viewBox='0 0 24 24' fill='currentColor'>
                <path d={mdiAccount} />
              </svg>
            </div>
          )}
        </div>
        <div>
          <h3 className='text-lg font-semibold text-grey-900'>
            {isEditing ? 'Edit User' : 'Create New User'}
          </h3>
          <p className='text-sm text-grey-600'>
            {isEditing
              ? 'Update user information and permissions'
              : 'Add a new user to the system'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-6'>
        {/* Personal Information */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Personal Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                First Name *
              </label>
              <Controller
                name='firstName'
                control={control}
                rules={{
                  required: 'First name is required',
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.firstName ? 'border-red-300' : 'border-grey-300'
                    }`}
                    placeholder='Enter first name'
                  />
                )}
              />
              <FormErrorMessage message={errors.firstName?.message} />
            </div>

            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Last Name *
              </label>
              <Controller
                name='lastName'
                control={control}
                rules={{
                  required: 'Last name is required',
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.lastName ? 'border-red-300' : 'border-grey-300'
                    }`}
                    placeholder='Enter last name'
                  />
                )}
              />
              <FormErrorMessage message={errors.lastName?.message} />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Contact Information
          </h4>
          <div className='grid grid-cols-1 gap-4'>
            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Email Address *
              </label>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='email'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-300' : 'border-grey-300'
                    }`}
                    placeholder={
                      isEditing && isAuthProfileLoading
                        ? 'Loading...'
                        : 'Enter email address'
                    }
                    disabled={isEditing}
                  />
                )}
              />
              <FormErrorMessage message={errors.email?.message} />
              {isEditing && (
                <p className='text-xs text-grey-500 mt-1'>
                  Email cannot be changed after user creation
                </p>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Phone Number
              </label>
              <Controller
                name='phone'
                control={control}
                rules={{
                  pattern: {
                    value: /^\+?[\d\s\-\(\)]{10,}$/,
                    message: 'Invalid phone number format',
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='tel'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.phone ? 'border-red-300' : 'border-grey-300'
                    }`}
                    placeholder={
                      isEditing && isAuthProfileLoading
                        ? 'Loading...'
                        : '+1 (555) 123-4567'
                    }
                    disabled={isEditing && isAuthProfileLoading}
                  />
                )}
              />
              <FormErrorMessage message={errors.phone?.message} />
            </div>
          </div>
        </div>

        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Role Assignment
          </h4>
          <div className='grid grid-cols-1 gap-4'>
            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                User Role *
              </label>
              <Controller
                name='role'
                control={control}
                rules={{
                  required: 'Role is required',
                }}
                render={({ field }) => (
                  <select
                    {...field}
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.role ? 'border-red-300' : 'border-grey-300'
                    }`}
                  >
                    {PLATFORM_ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              <FormErrorMessage message={errors.role?.message} />
              <p className='text-xs text-grey-500 mt-1'>
                This determines the user's permissions within the system
              </p>
            </div>
          </div>
        </div>

        {/* Account Status - Only show in edit mode */}
        {isEditing && (
          <div
            className={`rounded-lg p-4 ${isEnabled ? 'bg-green-50' : 'bg-red-50'}`}
          >
            <h4 className='text-sm font-medium text-grey-700 mb-4'>
              Account Status
            </h4>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-grey-900'>
                  {isEnabled ? 'Account Active' : 'Account Disabled'}
                </p>
                <p className='text-xs text-grey-500 mt-1'>
                  {isEnabled
                    ? 'User can log in and use the platform'
                    : 'User cannot log in and their reviews are hidden'}
                </p>
                {!isEnabled && user?.disabledAt && (
                  <p className='text-xs text-red-600 mt-1'>
                    Disabled on:{' '}
                    {new Date(user.disabledAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Controller
                name='enabled'
                control={control}
                render={({ field }) => (
                  <ToggleSwitch value={field.value} onChange={field.onChange} />
                )}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex items-center justify-end space-x-3 pt-6 border-t border-grey-200'>
          <Button
            type='button'
            label='Cancel'
            icon={mdiClose}
            color='light'
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            type='submit'
            label={isEditing ? 'Update User' : 'Create User'}
            icon={mdiCheck}
            color='primary'
            loading={loading}
            disabled={!isValid || (!isDirty && isEditing)}
          />
        </div>
      </form>
    </div>
  );
};
